/**
 * Utility to generate appropriate allowedFields from step context.
 *
 * Analyzes the step title and description to determine the best field types
 * for the request thread form. This enables form-first UI for structured requests.
 */

export interface GeneratedField {
  key: string;
  label: string;
  type: 'string' | 'string_long' | 'boolean' | 'enum' | 'enum_multi' | 'number' | 'date';
  required: boolean;
  description?: string;
  enumValues?: string[];
}

export interface GeneratedFieldsResult {
  fields: GeneratedField[];
  outputContract: { allowedKeys: string[] };
  uiMode?: 'form' | 'chat' | 'auto';
}

/**
 * Patterns for detecting decision-type tasks.
 * Format: "Decide X: option A vs option B" or "Choose between X, Y, Z"
 */
const DECISION_PATTERNS = [
  // "Decide X: A vs B" or "Decide X: A vs. B"
  /^(?:decide|choose|select|pick)\s+(.+?):\s*(.+?)\s+vs\.?\s+(.+)$/i,
  // "Decide between A and B" or "Choose between A or B"
  /^(?:decide|choose|select|pick)\s+between\s+(.+?)\s+(?:and|or)\s+(.+)$/i,
  // "X: A vs B" (implicit decision)
  /^(.+?):\s*(.+?)\s+vs\.?\s+(.+)$/i,
];

/**
 * Patterns for detecting explicit yes/no confirmation tasks (start with confirm/verify).
 * These take priority over other patterns.
 */
const EXPLICIT_CONFIRMATION_PATTERNS = [
  /^confirm\s+/i,
  /^verify\s+/i,
  /^acknowledge\s+/i,
];

/**
 * Patterns for detecting question-based confirmations.
 * These are checked after other patterns (date, numeric, etc.)
 */
const QUESTION_CONFIRMATION_PATTERNS = [
  /\?$/,  // Ends with question mark
];

/**
 * Patterns for detecting numeric input tasks.
 */
const NUMERIC_PATTERNS = [
  /\b(?:amount|quantity|number|count|price|cost|budget|total|how\s+many|how\s+much)\b/i,
];

/**
 * Patterns for detecting date input tasks.
 */
const DATE_PATTERNS = [
  /\b(?:date|when|deadline|due|schedule|appointment|time)\b/i,
];

/**
 * Patterns for detecting long-form text tasks.
 */
const LONG_TEXT_PATTERNS = [
  /\b(?:describe|explain|details|notes|comments|feedback|reason|why)\b/i,
];

/**
 * Parse a "vs" separated string into options.
 * "in-person vs certified mail" => ["in-person", "certified mail"]
 */
function parseVsOptions(str: string): string[] {
  return str
    .split(/\s+vs\.?\s+/i)
    .map(opt => opt.trim())
    .filter(opt => opt.length > 0);
}

/**
 * Convert a string to a valid key (snake_case).
 */
function toKey(str: string): string {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .substring(0, 50);
}

/**
 * Capitalize first letter of each word.
 */
function toLabel(str: string): string {
  return str
    .split(/\s+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Generate fields from step title and description.
 */
export function generateFieldsFromStep(
  stepTitle: string,
  stepDescription?: string | null
): GeneratedFieldsResult {
  const title = stepTitle.trim();
  const description = stepDescription?.trim() || '';

  // Try to detect decision pattern first (most specific)
  for (const pattern of DECISION_PATTERNS) {
    const match = title.match(pattern);
    if (match) {
      return generateDecisionFields(match, title, description);
    }
  }

  // Check for explicit confirmation (confirm/verify prefix) - takes priority
  if (EXPLICIT_CONFIRMATION_PATTERNS.some(p => p.test(title))) {
    return generateConfirmationFields(title, description);
  }

  // Check for numeric input (before question-based confirmation)
  if (NUMERIC_PATTERNS.some(p => p.test(title))) {
    return generateNumericFields(title, description);
  }

  // Check for date input (before question-based confirmation)
  if (DATE_PATTERNS.some(p => p.test(title))) {
    return generateDateFields(title, description);
  }

  // Check for long-form text
  if (LONG_TEXT_PATTERNS.some(p => p.test(title))) {
    return generateLongTextFields(title, description);
  }

  // Check for question-based confirmation (less specific, check after others)
  if (QUESTION_CONFIRMATION_PATTERNS.some(p => p.test(title))) {
    return generateConfirmationFields(title, description);
  }

  // Default: generic confirmation field
  return generateGenericFields(title, description);
}

/**
 * Generate fields for decision-type tasks.
 */
function generateDecisionFields(
  match: RegExpMatchArray,
  title: string,
  description: string
): GeneratedFieldsResult {
  // Extract the options from the match
  let options: string[] = [];
  let fieldLabel = 'Selection';

  if (match.length === 4) {
    // Pattern: "Decide X: A vs B" - match[1] is the topic, match[2] and match[3] are options
    fieldLabel = match[1].trim();
    options = [match[2].trim(), match[3].trim()];
  } else if (match.length === 3) {
    // Pattern: "Decide between A and B" - match[1] and match[2] are options
    options = [match[1].trim(), match[2].trim()];
  }

  // Check if any option contains "vs" for nested options
  const expandedOptions: string[] = [];
  for (const opt of options) {
    const subOptions = parseVsOptions(opt);
    if (subOptions.length > 1) {
      expandedOptions.push(...subOptions);
    } else {
      expandedOptions.push(opt);
    }
  }

  // Clean up options
  const cleanOptions = expandedOptions
    .map(opt => opt.replace(/^["']|["']$/g, '').trim())
    .filter(opt => opt.length > 0 && opt.length < 100);

  if (cleanOptions.length < 2) {
    // Couldn't parse options properly, fall back to generic
    return generateGenericFields(title, description);
  }

  const key = toKey(fieldLabel) || 'selection';

  return {
    fields: [{
      key,
      label: toLabel(fieldLabel),
      type: 'enum',
      required: true,
      description: `Choose one of the available options`,
      enumValues: cleanOptions,
    }],
    outputContract: { allowedKeys: [key] },
    uiMode: 'form',
  };
}

/**
 * Generate fields for confirmation/yes-no tasks.
 */
function generateConfirmationFields(
  title: string,
  description: string
): GeneratedFieldsResult {
  // Extract the thing being confirmed
  let confirmLabel = title
    .replace(/^(?:confirm|verify|acknowledge)\s+/i, '')
    .replace(/\?$/, '')
    .trim();

  if (confirmLabel.length > 50) {
    confirmLabel = 'Confirmation';
  }

  const key = toKey(confirmLabel) || 'confirmed';

  return {
    fields: [{
      key,
      label: toLabel(confirmLabel),
      type: 'boolean',
      required: true,
      description: description || `Confirm: ${title}`,
    }],
    outputContract: { allowedKeys: [key] },
    uiMode: 'form',
  };
}

/**
 * Generate fields for numeric input tasks.
 */
function generateNumericFields(
  title: string,
  description: string
): GeneratedFieldsResult {
  const key = 'amount';
  const label = 'Amount';

  return {
    fields: [{
      key,
      label,
      type: 'number',
      required: true,
      description: description || title,
    }],
    outputContract: { allowedKeys: [key] },
    uiMode: 'form',
  };
}

/**
 * Generate fields for date input tasks.
 */
function generateDateFields(
  title: string,
  description: string
): GeneratedFieldsResult {
  const key = 'date';
  const label = 'Date';

  return {
    fields: [{
      key,
      label,
      type: 'date',
      required: true,
      description: description || title,
    }],
    outputContract: { allowedKeys: [key] },
    uiMode: 'form',
  };
}

/**
 * Generate fields for long-form text tasks.
 */
function generateLongTextFields(
  title: string,
  description: string
): GeneratedFieldsResult {
  const key = 'response';
  const label = 'Response';

  return {
    fields: [{
      key,
      label,
      type: 'string_long',
      required: true,
      description: description || `Please provide: ${title}`,
    }],
    outputContract: { allowedKeys: [key] },
    uiMode: 'form',
  };
}

/**
 * Generate generic fallback for steps that don't match any pattern.
 * Always show at least one long-text field so the step is never empty
 * (practical combo: fallback when resolution is null).
 */
function generateGenericFields(
  title: string,
  description: string
): GeneratedFieldsResult {
  return {
    fields: [{
      key: 'response',
      label: 'Details',
      type: 'string_long',
      required: true,
      description: description || `Provide the information or details needed for: ${title}`,
    }],
    outputContract: { allowedKeys: ['response'] },
    uiMode: 'form',
  };
}
